import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import xor from 'lodash/xor'
import { FC, useCallback, useEffect, useState } from 'react'
import { Fade, IconButton, ItemImageArea, SkeletonText } from 'utoniq-ui'

import {
  ConsoleEventCollectionSelectBoxFragment,
  ConsoleEventCollectionSelectConnectionFragment,
  ItemType,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BottomSafeArea } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ItemIcon } from '@/components/organisms'
import { CollectionStatusCell } from '@/components/organisms/console/ConsoleEventItem/CollectionStatusCell'
import {
  FullscreenModal,
  ScrollFetcher,
} from '@/components/organisms/functionals'
import { ArtistThemeProvider, styleSets } from '@/components/styles'
import { useMessage, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'

type SelectCollectionModalProps = {
  artistId: string
  open: boolean
  onClose: () => void
  loading: boolean
  hasNextPage: boolean
  addingCollection: boolean
  collections: Maybe<ConsoleEventCollectionSelectConnectionFragment>
  onFetchMoreSelectCollection: () => Promise<void>
  onAddCollectionsToEvent: (collectionIds: string[]) => Promise<void>
}
export const SelectCollectionModal: FC<SelectCollectionModalProps> = ({
  artistId,
  open,
  onClose,
  loading,
  hasNextPage,
  collections,
  addingCollection,
  onFetchMoreSelectCollection,
  onAddCollectionsToEvent,
}) => {
  const { t } = useTranslation('consoleEventDetail')
  const { showError } = useMessage()
  const [selectedCollection, setSelectCollection] = useState<string[]>([])

  const handleSelectedCollection = useCallback((collectionId: string) => {
    setSelectCollection(prevState => xor(prevState, [collectionId]))
  }, [])

  const handleAddCollectionsToEvent = useCallback(() => {
    onAddCollectionsToEvent(selectedCollection)
      .then(() => onClose())
      .catch(showError)
  }, [onAddCollectionsToEvent, selectedCollection, showError, onClose])

  useEffect(() => {
    if (!open) {
      setSelectCollection([])
    }
  }, [open])

  return (
    <FullscreenModal open={open} onClose={onClose}>
      <Fade>
        <ArtistThemeProvider artistId={artistId}>
          <ConsoleBaseLayout
            margin
            header={{
              title: t('selectCollection', 'コレクションを選択'),
              right: (
                <IconButton.Close
                  edge="end"
                  size="medium"
                  color="white"
                  onClick={onClose}
                />
              ),
            }}
            customNavigation={
              selectedCollection.length ? (
                <FixedBottomCell
                  number={selectedCollection.length}
                  onAdd={handleAddCollectionsToEvent}
                  loading={addingCollection}
                />
              ) : null
            }
          >
            <ScrollFetcher
              componentProps={{ sx: { pt: 2 } }}
              nodes={collections?.edges?.map(({ node }, index) => (
                <Stack key={index} spacing={4}>
                  <CollectionSelectBox
                    collection={node}
                    onSelect={() => handleSelectedCollection(node.id)}
                    checked={
                      !!selectedCollection.find(
                        collectionId => collectionId === node.id
                      )
                    }
                  />
                </Stack>
              ))}
              emptyMessage={t('collectionItemEmptyMessage')}
              loading={loading}
              hasNextPage={hasNextPage}
              onFetchMore={onFetchMoreSelectCollection}
              loadingComponent={
                <Stack sx={{ py: 4 }}>
                  <CollectionItemSkeletonCell />
                </Stack>
              }
            />
          </ConsoleBaseLayout>
        </ArtistThemeProvider>
      </Fade>
    </FullscreenModal>
  )
}

type FixedBottomCellProps = {
  number: number
  onAdd: () => void
  loading: boolean
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  number,
  onAdd,
  loading,
}) => {
  const { t } = useTranslation('consoleEventDetail')
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.50' }}>
      <CellFrame sx={{ py: 4, bgcolor: 'grey.50', textAlign: 'center' }}>
        <Button
          size="large"
          sx={{ minWidth: 244 }}
          onClick={onAdd}
          loading={loading}
          disabled={loading}
        >
          {t('addSelectedCollection', { number })}
        </Button>
      </CellFrame>
    </BottomSafeArea>
  )
}

export type CollectionSelectBoxProps = {
  checked?: boolean
  collection: ConsoleEventCollectionSelectBoxFragment
  onSelect: (collectionId: string) => void
}
export const CollectionSelectBox: Organism<CollectionSelectBoxProps> = ({
  checked,
  collection,
  onSelect,
}) => {
  const { t } = useTranslation('consoleEventDetail')

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: checked ? 'primary.main' : 'grey.50',
      }}
    >
      <CardActionArea onClick={() => onSelect(collection.id)}>
        <CardContent sx={{ p: 5 }}>
          <Stack
            direction="row"
            spacing={4}
            alignItems="center"
            sx={{ position: 'relative' }}
          >
            <ItemImageArea size="small" src={collection.thumbnail?.uri ?? ''} />
            <Stack
              sx={{
                zIndex: 1,
                marginInline: 'auto',
                pl: 1,
                ml: '0 !important',
                position: 'absolute',
                top: 4,
                left: 0,
              }}
            >
              <ItemIcon type={ItemType.Image} />
            </Stack>
            <Stack flex={1}>
              <Stack direction="row" spacing={2}>
                <CollectionStatusCell status={collection.status} />
                <Typography variant="overline" color="text.secondary">
                  {t('publishedAt', { date: collection.formattedStartedAt })}
                </Typography>
              </Stack>
              <Typography variant="h6" sx={styleSets.lineCamp(1)}>
                {collection.title}
              </Typography>
              <Stack>
                <Typography variant="body2" color="text.secondary">
                  {t('createdAt', { date: collection.formattedCreatedAt })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('itemCount', { number: collection.itemCount })}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

const CollectionItemSkeletonCell = () => (
  <CellFrame>
    <Stack direction="row" spacing={2}>
      <Stack direction="row" flex={1} spacing={3}>
        <Skeleton
          variant="rectangular"
          width={120}
          height={90}
          sx={{ borderRadius: 2 }}
        />
        <Stack flex={1} spacing={1}>
          <Stack direction="row" spacing={1}>
            <Stack flex={1}>
              <SkeletonText variant="large" />
            </Stack>
            <Stack flex={5} justifyContent="center">
              <SkeletonText variant="small" />
            </Stack>
          </Stack>
          <SkeletonText variant="h3" />
          <Stack>
            <SkeletonText variant="small" numberOfLine={3} />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  </CellFrame>
)
